/**
 * Hostname of the live production deployment of Nymbl Staff.
 *
 * Per `terraform/variables.tf`, staging is `nos-staff-staging.nymbl.host` and
 * production is `nos-staff.nymbl.host`. Demo / sandbox accounts (and the
 * "Try as — sandbox accounts" login panel) must never surface in production —
 * see {@link isProductionHost}.
 */
export const PRODUCTION_HOST = "nos-staff.nymbl.host";

/**
 * True only for the live production host.
 *
 * Production is identified *positively* as the live `*.nymbl.host` app domain
 * (excluding the `*-staging.nymbl.host` host). Everything else is treated as
 * non-production so the demo-account affordances stay visible there:
 *
 * - local dev — `localhost`, `127.0.0.1`, `*.local`
 * - studio preview / sandbox / workstation hosts — `*.nymbl.studio`
 * - the staging deployment — any host containing `staging`
 * - any unrecognized host — fail-open to non-production (the panel only ever
 *   exposes synthetic sandbox logins, so the safe default is to show it
 *   everywhere except where we are sure it is production)
 *
 * Used as the single source of truth by both the client login panel
 * (`DemoAccountsPanel`) and the server-side `seedDemoUsers()` so the two gates
 * cannot drift apart.
 */
export function isProductionHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const host = hostname.trim().toLowerCase();
  if (!host) return false;

  // Never production: local development.
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) return false;
  // Never production: studio preview / sandbox / workstation hosts.
  if (host.endsWith(".nymbl.studio")) return false;
  // Never production: the staging deployment.
  if (host.includes("staging")) return false;

  // Production = the live nymbl.host app domain.
  return host === "nymbl.host" || host.endsWith(".nymbl.host");
}
